import React, { PropsWithChildren, useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as D3 from 'd3';
import { OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { message } from 'antd';

import Row from './Row';
import { ReactComponent as ArrowIcon } from './arrow_sort.svg';
import { FLAGS_EXCEED_MESSAGE, HEADER_NAMES } from '../../../config/constants';
import { useTablePropertiesDispatch, useTablePropertiesState } from '../../../store/hooks';
import { CustomTablePropsTypes, HeadsType, RowType } from './types';
import { useRouter } from '../../../helpers/customHooks';

import './styles.scss';

const CustomTable = <THeadsType extends HeadsType, TRowType extends RowType>
({
   classTable, isPinnedStyle, titleHeaders, isSelectedStyle, hasHeadersTooltip, headers, rows,
   isPersistent, withoutScroll, flagLimit, forceScope, customRows,
   linkSelectedRows, onChangeSelectedRows,
   default: defaultProperties, linkPinnedRows, zonesThresholds, isTargeted, hasNotes, alerting, isCombinedTable, isLiveSpeed
 }: PropsWithChildren<CustomTablePropsTypes<THeadsType, TRowType>>) => {

  const { tableSpecificScope } = useRouter();
  const scope = forceScope || tableSpecificScope;
  const { orderColumn = 0, pinnedRows = [], selectedRows = [], tableProperties } = useTablePropertiesState(scope);
  const {
    changeOrderColumn,
    changePinnedRows,
    changeSelectedRows,
    changeScroll,
    replaceTableProperties
  } = useTablePropertiesDispatch();
  const tbodyRef = useRef<HTMLTableSectionElement | null>(null);
  const refLeftFixedHorizontal = useRef('0px'); // for to remember
  const refTopFixedVertical = useRef('0px');
  const refElementScrollWidth = useRef(0);
  /*New improvments*/
  const thRefs = useRef<(HTMLTableCellElement | null)[]>([]);
  const [sortArrowLeft, setSortArrowLeft] = useState<number | null>(null);
  const theadRowRef = useRef<HTMLTableRowElement | null>(null);

  const fixedHorizontalSelectionRef = useRef<any>(null);
  const fixedVerticalSelectionRef = useRef<any>(null);
  const fixedHorizontalVerticalSelectionRef = useRef<any>(null);

  /** PINNED ROWS FIX */
  const theadRef = useRef<HTMLTableSectionElement | null>(null);
const [theadHeight, setTheadHeight] = useState(0);

const pinnedRowRefs = useRef<(HTMLTableRowElement | null)[]>([]);
const pinnedGraphRefs = useRef<(HTMLTableRowElement | null)[]>([]);
const [pinnedOffsets, setPinnedOffsets] = useState<{ main: number; graph: number }[]>([]);
const DEFAULT_ROW_HEIGHT = 50;
const arrowElRef = useRef<SVGSVGElement | null>(null);
const arrowStaticLeftRef = useRef(0);
const arrowIsFixedRef = useRef(false);

useLayoutEffect(() => {
  const sortedIdx = orderColumn !== 0 ? Math.abs(orderColumn) - 1 : -1;
  const thEl = sortedIdx >= 0 ? thRefs.current[sortedIdx] : null;
  if (!thEl) {
    arrowIsFixedRef.current = false;
    return;
  }
  arrowIsFixedRef.current = !!headers[sortedIdx]?.isColumnFixedY;
  arrowStaticLeftRef.current = thEl.offsetLeft + thEl.offsetWidth / 2 - 8;
  // apply immediately so it's correct before the next scroll frame runs
  if (arrowElRef.current) {
    const scrollAdjustment = arrowIsFixedRef.current ? parseFloat(refLeftFixedHorizontal.current) || 0 : 0;
    arrowElRef.current.style.left = (arrowStaticLeftRef.current + scrollAdjustment) + 'px';
  }
}, [orderColumn, headers, rows.length]);

const pinnedRowsInOrder = React.useMemo(() => {
  return pinnedRows
    .map(id => rows.find(row => row.id === id))
    .filter((row): row is TRowType => Boolean(row));
}, [pinnedRows, rows]);

useLayoutEffect(() => {
  const offsets: { main: number; graph: number }[] = [];
  let runningTop = theadHeight;
  pinnedRowsInOrder.forEach((_, index) => {
    const mainEl = pinnedRowRefs.current[index];
    const graphEl = pinnedGraphRefs.current[index];
    const mainHeight = mainEl?.offsetHeight ?? DEFAULT_ROW_HEIGHT;
    const graphHeight = graphEl?.offsetHeight ?? 0;
    offsets.push({ main: runningTop, graph: runningTop + mainHeight });
    runningTop += mainHeight + graphHeight;
  });
  setPinnedOffsets(offsets);
}, [pinnedRowsInOrder, theadHeight]);

const firstPinnedRowId = pinnedRows[0] ?? null;

useEffect(() => {
  if (!theadRef.current) return;
  const observer = new ResizeObserver(() => {
    setTheadHeight(theadRef.current!.offsetHeight);
  });
  observer.observe(theadRef.current);
  return () => observer.disconnect();
}, []);



  /** EN PINNED ROWS FIX */


  const debounce = (fn: (...args: any[]) => void, delay: number) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const debounced = (...args: any[]) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
  debounced.cancel = () => {
    if (timeoutId) clearTimeout(timeoutId);
  };
  return debounced;
};

useEffect(() => () => {
  debouncedChangeScroll.cancel();
  debouncedStopScrolling.cancel(); 
}, []);

useEffect(() => {
  fixedHorizontalSelectionRef.current = D3.selectAll('.OGCY-fixed-horizontal.' + selectorScope);
  fixedVerticalSelectionRef.current = D3.selectAll('.OGCY-fixed-vertical.' + selectorScope);
  fixedHorizontalVerticalSelectionRef.current = D3.selectAll('.OGCY-fixed-horizontal.OGCY-fixed-vertical.' + selectorScope);
}, [rows.length, headers, scope, customRows, pinnedRows.length]); // re-query whenever the SET of matching elements could change

useLayoutEffect(() => {
  const sortedIndex = orderColumn !== 0 ? Math.abs(orderColumn) - 1 : -1;
  const thEl = sortedIndex >= 0 ? thRefs.current[sortedIndex] : null;
  const rowEl = theadRowRef.current;
  if (thEl && rowEl) {
    const thRect = thEl.getBoundingClientRect();
    const rowRect = rowEl.getBoundingClientRect();
    setSortArrowLeft(thRect.left - rowRect.left + thRect.width / 2 - 8);
  } else {
    setSortArrowLeft(null);
  }
}, [orderColumn, headers, rows.length]);
/** END FIRS IMPROVMENTS */
  useEffect(() => {
    if (isPersistent) {
      const reducer = 'tableProperties';
      const recoveredStore = JSON.parse(localStorage.getItem('store') || '{}');
      if (recoveredStore[reducer] && recoveredStore[reducer][scope]) {
        replaceTableProperties(
          recoveredStore[reducer][scope].orderColumn,
          recoveredStore[reducer][scope].pinnedRows,
          recoveredStore[reducer][scope].selectedRows,
          recoveredStore[reducer][scope].scroll,
          defaultProperties,
          isPersistent,
          forceScope,
          linkPinnedRows
        )();
      } else {
        replaceTableProperties(orderColumn, pinnedRows, selectedRows, tableProperties.scroll || {
          top: 0,
          left: 0
        }, defaultProperties, isPersistent, forceScope, linkPinnedRows)();
      }
    } else {
      replaceTableProperties(orderColumn, pinnedRows, selectedRows, tableProperties.scroll || {
        top: 0,
        left: 0
      }, defaultProperties, isPersistent, forceScope, linkPinnedRows)();
    }
  }, [scope, customRows]);
  const [hasScroll, setHasScroll] = useState(false);
  const selectorScope = scope.split('_').join('-').split(':').join('-');

   /**UPDATES REPLACEING D3 with css */
  const applyFixedItems = () => {
  fixedVerticalSelectionRef.current?.style('top', refTopFixedVertical.current);
  fixedHorizontalSelectionRef.current?.style('left', refLeftFixedHorizontal.current);
  fixedHorizontalVerticalSelectionRef.current?.style('left', refLeftFixedHorizontal.current);
  fixedHorizontalVerticalSelectionRef.current?.style('top', refTopFixedVertical.current);
  if (arrowElRef.current) {
    const scrollAdjustment = arrowIsFixedRef.current ? (parseFloat(refLeftFixedHorizontal.current) || 0) : 0;
    arrowElRef.current.style.left = (arrowStaticLeftRef.current + scrollAdjustment) + 'px';
  }

  };
const animationFrameId = useRef<number | null>(null);
const didRestoreScroll = useRef(false);

useEffect(() => {
  didRestoreScroll.current = false; // allow restore again when scope changes
}, [scope]);

useEffect(() => {
  if (
    !didRestoreScroll.current &&
    tableBodyRef.current &&
    tableProperties.scroll
  ) {
    tableBodyRef.current.scrollTop = Math.min(
      tableBodyRef.current.scrollHeight,
      tableProperties.scroll.top
    );
    tableBodyRef.current.scrollLeft = Math.min(
      tableBodyRef.current.scrollWidth,
      tableProperties.scroll.left
    );
    didRestoreScroll.current = true;
  }
});

  const debouncedChangeScroll = useRef(
  debounce((top: number, left: number) => {
    changeScroll({ top, left }, !!isPersistent, forceScope)();
  }, 200)
).current;

const debouncedStopScrolling = useRef(
  debounce(() => {
    tableBodyRef.current?.classList.remove('is-scrolling');
  }, 150)
).current;

const changeValuesFixedItems = ({ target }: any) => {
    tableBodyRef.current?.classList.add('is-scrolling');   // ← add this line
  debouncedStopScrolling(); 
  refTopFixedVertical.current = target.scrollTop + 'px';

  if (refElementScrollWidth.current - target.scrollLeft >= target.clientWidth) {
    refLeftFixedHorizontal.current = target.scrollLeft + 'px';
  } else if (tableBodyRef.current) {
    tableBodyRef.current.scrollLeft = refElementScrollWidth.current - target.clientWidth;
  }

  if (animationFrameId.current) {
    cancelAnimationFrame(animationFrameId.current);
  }
  animationFrameId.current = requestAnimationFrame(() => {
    applyFixedItems();
  });

  debouncedChangeScroll(
    Math.min(target.scrollHeight, target.scrollTop),
    Math.min(target.scrollWidth, target.scrollLeft)
  );
};
/** END OF UPDATE REPLACING D3 */
  useEffect(() => {
    if (tbodyRef.current && !withoutScroll) {
      setHasScroll(tbodyRef.current.scrollHeight > tbodyRef.current.clientHeight);
    }
    // restarting positions
    refLeftFixedHorizontal.current = '0px'; // for to remember
    refTopFixedVertical.current = '0px';
    const fixedHorizontalDivs = D3.selectAll('td.' + selectorScope);
    fixedHorizontalDivs.style('top', '0px');
    fixedHorizontalDivs.style('left', '0px');
    if (tableBodyRef.current) {
      tableBodyRef.current.scrollLeft = 0;
      tableBodyRef.current.scrollTop = 0;
      refElementScrollWidth.current = tableBodyRef.current.scrollWidth;
    }
    applyFixedItems();
  }, [rows.length, scope, customRows]); // add pinnedRows to move restarting the scroll

  useEffect(applyFixedItems, [pinnedRows, rows]); // to move the new fixed rows and the new rows
  const tableBodyRef = useRef<HTMLDivElement | null>(null);
  const allIds: string[] = [];
  let isAllSelected = !!selectedRows.length && selectedRows.length === rows.length;
  let isAllPinned = !!pinnedRows.length && pinnedRows.length === rows.length;

  const order = (a: RowType, b: RowType) => {
    if (orderColumn === 0) {
      return 0;
    }
    const idx: number = Math.abs(orderColumn) - 1;
    if (idx >= a.columns.length) {
      return 0;
    }
    const order = orderColumn < 0 ? 1 : -1;
    if (a.columns[idx].order) {
      // @ts-ignore
      return order * a.columns[idx].order(a.columns[idx].content || '', b.columns[idx].content || '');
    }
    if (!Number.isNaN(+((a.columns[idx].content) || '_')) && !Number.isNaN(+(b.columns[idx].content || '_'))) {
      return order * (+(a.columns[idx].content || '') - +(b.columns[idx].content || ''));
    }
    if ((typeof a.columns[idx].content) === 'string') {
      return order * ((a.columns[idx].content + '').localeCompare(b.columns[idx].content + ''));
    }
    return 0;
  };

  [...rows].sort(order).forEach(row => { // sort to add all ids in order
    let sw = false;
    row.columns.forEach(cell => {
      if (isSelectedStyle) {
        sw = sw || !!cell.isCheckboxCell;
      }
      if (isPinnedStyle && !isTargeted) {
        sw = sw || !!cell.isPinFlagCell;
      }
    });
    if (sw) {
      isAllPinned = isAllPinned && pinnedRows.some(id => id === row.id);
      isAllSelected = isAllSelected && selectedRows.some(id => id === row.id);
      allIds.push(row.id);
    }
  });

  const pinnedRowCheck = <img alt="flag" src="/assets/flag-03.svg" height="13" width="13" />;
  const unpinnedRowCheck = <img alt="flag" src="/assets/flag-02.svg" height="13" width="13" />;
  const processedHeaders = headers.map((header, index) => {
    let head;
    head = header.content;
    if (header.content && header.content.props && header.content.props.children) {
      if (header.content.props.children.length > 2) {
        head = header.content.props.children[0] + header.content.props.children[2];
      } else {
        head = header.content.props.children;
      }
      if (header.content.props.children.props && header.content.props.children.props.children) {
        head = header.content.props.children.props.children;
      }
    }

    if (header.isCheckboxCell) {
      header.content = (
        <div className="styled-checkbox-label-checked"
             style={{ background: isAllSelected ? '#007ABA' : undefined }} />
      );
    }
    if (header.isPinFlagCell) {
      if (flagLimit) {
        header.content = pinnedRows.length === flagLimit ? pinnedRowCheck : unpinnedRowCheck;
      } else {
        header.content = isAllPinned ? pinnedRowCheck : unpinnedRowCheck;
      }
    }

    const thCell = header.print ? header.print(header.content || '') : header.content;
    const tooltip = (val?: JSX.Element) => {
      if (val) {
        return (
          <Tooltip id="tool">
            <div style={{ color: '#0A6F9A' }}>{val}</div>
          </Tooltip>
        );
      }
      return <Tooltip hidden={true} id="no_tooltip" />;
    };

    return <th
      key={index}
      style={header.style}
      ref={(el) => { thRefs.current[index] = el; }}
      className={
        (header.class ? header.class : '') +
        ' ' + selectorScope +
        (header.isColumnFixedY ? ' OGCY-fixed-horizontal' : '') +
        // (isPinnedStyle ? ' OGCY-fixed-vertical' : '') +
        (header.rightShadow ? ' nvd-custom-table-tr-td-right-shadow' : '')
        // (header.rightShadow ? ' nvd-custom-table-tr-td-right-shadow-corn' : '')
      }
      onClick={() => {
        if (header.isOrderly) {
          if (orderColumn === index + 1) {
            changeOrderColumn(-orderColumn, !!isPersistent, forceScope)();
          } else if (orderColumn === -(index + 1)) {
            changeOrderColumn(0, !!isPersistent, forceScope)();
          } else {
            changeOrderColumn(index + 1, !!isPersistent, forceScope)();
          }
        }
        if (header.isSelectable) {
          if (isSelectedStyle) {
            if (isAllSelected) {
              changeSelectedRows([], !!isPersistent, forceScope, linkSelectedRows, onChangeSelectedRows)();
            } else {
              changeSelectedRows([...allIds], !!isPersistent, forceScope, linkSelectedRows, onChangeSelectedRows)();
            }
          }
          if (isPinnedStyle && !isTargeted) {
            if (isAllPinned) {
              changePinnedRows([], !!isPersistent, forceScope, linkPinnedRows)();
            } else {
              let unmutedIds = [...allIds];
              // PV restriction at the moment
              if (flagLimit) {
                if (pinnedRows.length === flagLimit) {
                  unmutedIds = [];
                } else {
                  unmutedIds.splice(flagLimit);
                }
              }
              changePinnedRows(unmutedIds, !!isPersistent, forceScope, linkPinnedRows)();
            }
          }
        }
        if (header.onClick) {
          header.onClick();
        }
      }}
    >
      {header.hoverButton && (
        <span
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          className="only-on-hover"
        >
         {(header.hoverButton as (metric: string) => JSX.Element)(header.metric || '')}
      </span>
      )}
      {hasHeadersTooltip && !header.isCheckboxCell && !header.isPinFlagCell ? (
        <OverlayTrigger
          placement="bottom"
          overlay={zonesThresholds? zonesThresholds[head] && !zonesThresholds[head].includes('undefined') ? tooltip(zonesThresholds[head]) : 
            HEADER_NAMES[head] ? tooltip(HEADER_NAMES[head]) : tooltip(head) :tooltip(head)}
          key={index}
        >
          <span>{thCell}</span>
        </OverlayTrigger>
      ) : <span>{thCell}</span>}
    </th>;
  });
  /**UPDATE ARROW ICON TO MATCH PREVIOUS BEHAVIOUR */
const sortedIndex = orderColumn !== 0 ? Math.abs(orderColumn) - 1 : -1;
const sortedHeader = sortedIndex >= 0 ? headers[sortedIndex] : null;
const arrowZIndex = sortedHeader?.isColumnFixedY ? 176 : 170;
  const sortArrowEl = orderColumn !== 0 && sortArrowLeft !== null ? (
  <ArrowIcon
    ref={arrowElRef}
    className={'arrow-icon' + (orderColumn < 0 ? ' arrow-icon-up' : '')}
    style={{
      position: 'absolute',
      left: sortArrowLeft,
      bottom: '-7px',
      zIndex: arrowZIndex,
      pointerEvents: 'none',
    }}
  />
) : null;
/**END ICON UPDATE */
  const processTitleHeaders = titleHeaders && titleHeaders.map((header, index) => (
    <th
      key={index}
      colSpan={header.colSpan}
      className={
        selectorScope + ' ' +
        header.class + ' '+
        (header.isCustomHeader? 'custom-fix':'')+
        (header.isColumnFixedY ? ' OGCY-fixed-horizontal' : '') +
        (header.rightShadow ? ' nvd-custom-table-tr-td-right-shadow' : '')
      }
      style={header.style}
    >
      {header.content}
    </th>
  ));
  rows.sort(order);
  const processRows = [];
  if (isPinnedStyle && !isTargeted) {
    /**NEW PINNED ROWS UPDATE */
processRows.push(...pinnedRowsInOrder.map((row, index) => {
  const rowWithPinIcon = {
    ...row,
    columns: row.columns.map((col, colIndex) =>
      colIndex === 0
        ? {
            ...col,
            content: pinnedRowCheck,
            onClick: () => {
              changePinnedRows(pinnedRows.filter(pinned => pinned !== row.id), !!isPersistent, forceScope, linkPinnedRows)();
            },
          }
        : col
    ),
  };
    const offsets = pinnedOffsets[index] ?? {
    main: theadHeight + index * DEFAULT_ROW_HEIGHT,
    graph: theadHeight + (index + 1) * DEFAULT_ROW_HEIGHT,
  };
  return (
    <Row
      data={rowWithPinIcon}
      key={'Row' + row.id}
      pinned
      pinnedMainTop={offsets.main}
      pinnedGraphTop={offsets.graph}
      rowRef={(el: HTMLTableRowElement | null) => { pinnedRowRefs.current[index] = el; }}
      graphRowRef={(el: HTMLTableRowElement | null) => { pinnedGraphRefs.current[index] = el; }}
      headers={headers}
      hasScroll={hasScroll}
      selectorScope={selectorScope}

    />
  );
}));
   processRows.push(...rows
  .filter(row => pinnedRows.indexOf(row.id) === -1)
  .map(row => {
    const rowWithPinIcon = {
      ...row,
      columns: row.columns.map((col, colIndex) =>
        colIndex === 0
          ? {
              ...col,
              content: unpinnedRowCheck,
              onClick: () => {
                if (!flagLimit || pinnedRows.length < flagLimit) {
                  changePinnedRows([...pinnedRows, row.id], !!isPersistent, forceScope, linkPinnedRows)();
                } else {
                  message.warning({ content: FLAGS_EXCEED_MESSAGE, className: 'pv-antd-message-alert' });
                }
              },
            }
          : col
      ),
    };
    return (
      <Row
        data={rowWithPinIcon}
        key={'Row' + row.id}
        excluded={row.columns[1].isExcludeCell}
        hasNotes={hasNotes}
        headers={headers}
        hasScroll={hasScroll}
        selectorScope={selectorScope}
      />
    );
  })
);
/**END OF NEW PINNED ROWS */
  } else if (isSelectedStyle) {
    processRows.push(...rows
      .map(row => {
        const selected = selectedRows.indexOf(row.id) !== -1;
        row.columns.map((cell, index) => {
          if (headers[index].isCheckboxCell) {
            cell.onClick = () => {
              if (selected) {
                changeSelectedRows(selectedRows.filter(rowId => rowId !== row.id), !!isPersistent, forceScope, linkSelectedRows, onChangeSelectedRows)();
              } else {
                changeSelectedRows([...selectedRows, row.id], !!isPersistent, forceScope, linkSelectedRows, onChangeSelectedRows)();
              }
            };
          }
          return cell;
        });
        return (
          <Row
            data={row}
            key={'Row' + row.id}
            selected={selected}
            headers={headers}
            hasScroll={hasScroll}
            selectorScope={selectorScope}
          />
        );
      })
    );
  } else {
    processRows.push(...rows.map(row => (
      <Row data={row} key={'Row' + row.id} headers={headers} hasScroll={hasScroll} selectorScope={selectorScope} />
    )));
  }
  const tableContent =  <Table
      className={(classTable ? classTable : '') + (isPinnedStyle || alerting ? ' OGCY-Table' : '') + ' nvd-custom-table'}
      key={scope}
      id={'TABLE_' + scope}
    >
      <thead ref={theadRef} style={!isCombinedTable? {top:0}: {}}>
      {processTitleHeaders && (
        <tr className={'nvd-custom-table-thead-tr-title-head'}>
          {processTitleHeaders}
        </tr>
      )}
      <tr
       className={'nvd-custom-table-thead-tr-head ' + selectorScope} style={{ position: 'relative' }} ref={theadRowRef}>
        {processedHeaders}
        {sortArrowEl}
      </tr>
      </thead>
      <tbody
        ref={tbodyRef}
        className={hasScroll ? 'tbody-with-scroll' : undefined}
        style={withoutScroll ? { overflow: 'visible' } : undefined}
      >
      {processRows}
      </tbody>
    </Table>;
  return (
    isCombinedTable? tableContent :
    <div className="nvd-custom-table-scroll-wrapper" ref={tableBodyRef} onScroll={changeValuesFixedItems} style={isLiveSpeed? {height: '317px'}:{}}>
      {tableContent}
    </div>
  );
};

export default CustomTable;
export * from './types';

